import { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import * as Sentry from '@sentry/react'

import {
  PRMarinaNode,
  useEvent,
  useSceneRootNode,
} from '@sketch-hq/sketch-web-renderer'

import { buildSketchSceneTreeFromNode, SketchElement } from '@sketch/inspector'

/**
 * Build the tree of sketch elements from the node the canvas is rooted on.
 * The renderer's nodes are what it draws from; we reconstruct the tree of
 * layers like the one shown to users in the Sketch app.
 *
 * Rooted on the *scene*, not the file. In a focus view the scene is re-rooted
 * on the focused entity, and the renderer only requests fragments inside that
 * root - so a tree built from the file would carry frames from the rest of the
 * page whose fragments are never coming, and never count as fully loaded. It
 * also means the tree needs no trimming afterwards: it already describes what
 * is on screen.
 *
 * `useSceneRootNode` re-reads on `sceneChange`, so this follows the canvas
 * being re-rooted without the provider being remounted.
 */
export function useSketchSceneTree(isInspectorActive: boolean) {
  const [hasFailed, setFailed] = useState(false)
  const [sketchRootElement, setSketchRootElement] =
    useState<SketchElement | null>(null)

  const sceneRootNode = useSceneRootNode()

  const createSketchRootElement = useCallback((rootNode: PRMarinaNode) => {
    try {
      const { rootElement } = buildSketchSceneTreeFromNode(rootNode)
      setSketchRootElement(rootElement)
      setFailed(false)
    } catch (e) {
      Sentry.withScope(scope => {
        scope.setTag('sketch_inspector', 'InvalidSketchScene')
        Sentry.captureException(e)
      })

      setSketchRootElement(null)
      setFailed(true)
    }
  }, [])

  const updateBuildScene = useMemo(() => {
    return debounce(() => {
      if (!sceneRootNode || !isInspectorActive) {
        return
      }

      createSketchRootElement(sceneRootNode)
    }, 1000)
  }, [sceneRootNode, createSketchRootElement, isInspectorActive])

  useEffect(() => {
    if (!sceneRootNode || !isInspectorActive) {
      return
    }

    createSketchRootElement(sceneRootNode)
  }, [sceneRootNode, createSketchRootElement, isInspectorActive])

  useEvent('fragmentLoad', () => {
    updateBuildScene?.()
  })

  return {
    sketchRootElement,
    hasFailed,
  }
}
